// Receiver proof is distinct from the provider PKCE verifier, which stays server-side.
export function receiverSecret(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  return base64url(bytes);
}
export async function receiverChallenge(secret: string): Promise<string> {
  return base64url(
    new Uint8Array(
      await crypto.subtle.digest("SHA-256", new TextEncoder().encode(secret)),
    ),
  );
}
function base64url(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}
export async function startCenterLogin(
  origin: URL,
  target: "web" | "extension",
  challenge: string,
): Promise<string> {
  const response = await fetch(new URL("/v1/auth/center/start", origin), {
    method: "POST",
    credentials: "same-origin",
    cache: "no-store",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ target, receiver_challenge: challenge }),
  });
  if (!response.ok) throw new Error("CENTER_UNAVAILABLE");
  const body: unknown = await response.json();
  if (
    typeof body !== "object" ||
    body === null ||
    !("authorization_url" in body) ||
    typeof body.authorization_url !== "string"
  )
    throw new Error("INVALID_RESPONSE");
  const url = new URL(body.authorization_url);
  if (
    url.protocol !== "https:" &&
    !(
      url.protocol === "http:" &&
      ["localhost", "127.0.0.1"].includes(url.hostname)
    )
  )
    throw new Error("INVALID_RESPONSE");
  return url.href;
}
export interface HostedCenterTransaction {
  authorization_url: string;
  transaction: string;
  expires_at: string;
}
export async function startHostedCenterLogin(
  origin: URL,
  target: "web" | "extension",
  challenge: string,
  signal?: AbortSignal,
): Promise<HostedCenterTransaction> {
  const response = await fetch(new URL("/v1/auth/center/start", origin), {
    method: "POST",
    credentials: "same-origin",
    cache: "no-store",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ target, receiver_challenge: challenge }),
    signal: signal ?? null,
  });
  if (!response.ok) throw new Error("CENTER_UNAVAILABLE");
  const value = (await response.json()) as Partial<HostedCenterTransaction>;
  if (
    typeof value.transaction !== "string" ||
    !/^[A-Za-z0-9_-]{43}$/.test(value.transaction) ||
    typeof value.authorization_url !== "string" ||
    typeof value.expires_at !== "string" ||
    !Number.isFinite(Date.parse(value.expires_at)) ||
    Date.parse(value.expires_at) <= Date.now()
  )
    throw new Error("INVALID_RESPONSE");
  const url = new URL(value.authorization_url);
  if (
    (url.protocol !== "https:" &&
      !(
        url.protocol === "http:" &&
        ["localhost", "127.0.0.1"].includes(url.hostname)
      )) ||
    url.username ||
    url.password ||
    url.search ||
    url.hash ||
    !/^\/login\/t\/[A-Za-z0-9_-]+$/.test(url.pathname)
  )
    throw new Error("INVALID_RESPONSE");
  return value as HostedCenterTransaction;
}
export async function centerHandoff(
  origin: URL,
  transaction?: string,
  signal?: AbortSignal,
): Promise<string> {
  const response = await fetch(new URL("/v1/auth/center/handoff", origin), {
    method: "POST",
    credentials: "same-origin",
    cache: "no-store",
    signal: signal ?? AbortSignal.timeout(15000),
    ...(transaction
      ? {
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ transaction }),
        }
      : {}),
  });
  if (!response.ok) throw new Error("CENTER_LOGIN_FAILED");
  const body: unknown = await response.json();
  if (
    typeof body !== "object" ||
    body === null ||
    !("exchange_code" in body) ||
    typeof body.exchange_code !== "string" ||
    !/^[A-Za-z0-9_-]{43}$/.test(body.exchange_code)
  )
    throw new Error("INVALID_RESPONSE");
  return body.exchange_code;
}
